// frontend/src/hooks/useWorkshopHealth.js
// Every number the workshop dashboard shows is derived here.

import { useMemo } from 'react';
import { signal } from '../theme/iparc';

export const BAY_COUNT = 6; // workshop capacity — move to workshop profile when available

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export const oilRemaining = (v) => {
  const odo = num(v.odometerReading);
  const next = num(v.nextOilChangeOdometer);
  if (!next) return null;
  return next - odo;
};

export const oilDue = (v) => { const r = oilRemaining(v); return r !== null && r <= 500; };

export const oilStatus = (v) => {
  const r = oilRemaining(v);
  const interval = num(v.oilChangeInterval) || 5000;
  const unit = v.distanceUnit === 'mi' ? 'mi' : 'km';
  if (r === null) return { color: signal.ok, label: '—', pct: '0%' };
  const pct = `${Math.min(100, Math.max(3, ((interval - r) / interval) * 100))}%`;
  const f = (x) => x.toLocaleString('fr-FR');
  if (r <= 0)    return { color: signal.danger, label: `dépassé ${f(Math.abs(r))} ${unit}`, pct: '100%' };
  if (r <= 500)  return { color: signal.danger, label: `${f(r)} ${unit} restants`, pct };
  if (r <= 1200) return { color: signal.warn,   label: `${f(r)} ${unit} restants`, pct };
  return { color: signal.ok, label: `${f(r)} ${unit} restants`, pct };
};

export const severity = (v) => {
  const r = oilRemaining(v);
  if (r === null) return 'ok';
  if (r <= 0) return 'danger';
  if (r <= 1200) return 'warn';
  return 'ok';
};

// Request status → visual treatment + the action the workshop should take next.
// Keys must match backend/models/maintenanceRequestModel.js exactly:
// pending | accepted | rescheduled | confirmed | in-progress | completed |
// cancelled | declined. Note the HYPHEN in 'in-progress' and that this app has
// no 'quoted' status — an unmapped key would silently fall back to the
// "En attente / Établir un devis" treatment and tell the workshop to quote a
// job already on the ramp.
export const REQUEST_STATUS = {
  pending:       { label: 'En attente',   color: signal.danger, tint: signal.dangerTint, action: 'Établir un devis' },
  accepted:      { label: 'Accepté',      color: signal.warn,   tint: signal.warnTint,   action: 'Planifier' },
  // 'Planifier', not 'Confirmer la date': the workshop books the slot itself
  // now, so this opens the same slot picker as 'accepted' rather than waiting
  // on a client confirmation no shipped screen can send.
  rescheduled:   { label: 'Reprogrammé',  color: signal.warn,   tint: signal.warnTint,   action: 'Planifier' },
  confirmed:     { label: 'Confirmé',     color: signal.ok,     tint: signal.okTint,     action: 'Démarrer' },
  'in-progress': { label: 'En cours',     color: signal.ok,     tint: signal.okTint,     action: 'Saisir' },
  completed:     { label: 'Terminé',      color: signal.ok,     tint: signal.okTint,     action: 'Facturer' },
  cancelled:     { label: 'Annulé',       color: '#8F877B',     tint: 'rgba(143,135,123,.12)', action: null },
  declined:      { label: 'Refusé',       color: '#8F877B',     tint: 'rgba(143,135,123,.12)', action: null },
};

const CHANNELS = { app: 'Application', whatsapp: 'WhatsApp', walkin: 'Sans RDV' };

const hoursSince = (d) => (d ? Math.max(0, Math.round((Date.now() - new Date(d)) / 36e5)) : 0);

export default function useWorkshopHealth({ vehicles = [], requests = [], quotations, invoices }) {
  return useMemo(() => {
    const n = vehicles.length;

    // ── Fleet condition ─────────────────────────────────────────────────────
    const sevOrder = { ok: 0, warn: 1, danger: 2 };
    const sevColor = { ok: signal.ok, warn: signal.warn, danger: signal.danger };
    const sevs = vehicles.map(severity).sort((a, b) => sevOrder[a] - sevOrder[b]);
    const segments = sevs.map(s => sevColor[s]);
    const cnt = (k) => sevs.filter(x => x === k).length;
    const dueCount = vehicles.filter(oilDue).length;

    // ── Request pipeline ────────────────────────────────────────────────────
    const open = requests.filter(r => !['completed', 'cancelled', 'declined', 'rejected'].includes(r.status));
    const byStatus = (k) => open.filter(r => r.status === k).length;
    const pendingCount = byStatus('pending');
    const inBay = byStatus('confirmed') + byStatus('in-progress');

    const pipeline = [
      { label: 'À deviser', count: pendingCount, color: signal.danger },
      { label: 'Acceptés',  count: byStatus('accepted') + byStatus('rescheduled'), color: signal.warn },
      { label: 'En cours',  count: inBay, color: signal.ok },
    ].map(p => ({ ...p, pct: `${Math.round((p.count / Math.max(1, open.length)) * 100)}%` }));

    const queue = open
      .map(r => {
        const st = REQUEST_STATUS[r.status] || REQUEST_STATUS.pending;
        const hours = hoursSince(r.createdAt);
        const stale = r.status === 'pending' && hours >= 24;
        return {
          id: r._id, raw: r,
          plate: r.vehicleId?.plateNumber || r.vehicle?.plateNumber || r.plateNumber || '—',
          client: r.requester?.name || r.owner?.name || r.clientName || 'Client',
          service: r.serviceType || r.description || 'Demande',
          channel: CHANNELS[r.source] || CHANNELS.app,
          status: st.label, color: st.color, tint: st.tint, action: st.action,
          hours, urgent: r.status === 'pending',
          age: hours >= 24 ? `il y a ${Math.floor(hours / 24)} j` : `il y a ${hours} h`,
          ageColor: stale ? signal.danger : hours >= 12 && r.status === 'pending' ? signal.warn : '#8F877B',
        };
      })
      .sort((a, b) => (Number(b.urgent) - Number(a.urgent)) || (b.hours - a.hours));

    const oldestPendingH = Math.max(0, ...queue.filter(q => q.urgent).map(q => q.hours));

    // ── Money ───────────────────────────────────────────────────────────────
    const fmtM = (x) => num(x).toLocaleString('fr-FR');
    const money = [
      { key: 'pending', label: 'Devis en attente', amount: fmtM(quotations?.pendingTotal),
        color: signal.warn, tint: signal.warnTint, state: 'À relancer',
        sub: `${num(quotations?.pendingQuotations)} devis sans réponse client`,
        cta: 'Voir les devis', href: '/workshop/quotations?status=pending' },
      { key: 'confirmed', label: 'Devis confirmés', amount: fmtM(quotations?.confirmedTotal),
        color: signal.ok, tint: signal.okTint, state: 'Engagé',
        sub: `${num(quotations?.confirmedQuotations)} devis acceptés`,
        cta: 'Planifier les travaux', href: '/workshop/quotations?status=confirmed' },
      { key: 'unpaid', label: 'Factures impayées', amount: fmtM(invoices?.unpaidTotal),
        color: signal.danger, tint: signal.dangerTint, state: 'Dû',
        sub: `${num(invoices?.unpaidInvoices)} factures échues`,
        cta: 'Relancer les clients', href: '/workshop/invoices?status=unpaid' },
    ];

    // ── Bay load ────────────────────────────────────────────────────────────
    const load = Math.round((inBay / BAY_COUNT) * 100);
    const bays = [
      { label: 'Postes occupés', num: `${inBay}/${BAY_COUNT}`,
        pct: `${Math.min(100, Math.round((inBay / BAY_COUNT) * 100))}%`,
        color: load >= 90 ? signal.danger : load >= 60 ? signal.warn : signal.ok },
      { label: 'À deviser', num: String(pendingCount),
        pct: `${Math.round((pendingCount / Math.max(1, open.length)) * 100)}%`,
        color: pendingCount ? signal.danger : signal.ok },
      { label: 'Vidanges dues', num: `${dueCount}/${n}`,
        pct: n ? `${Math.round((dueCount / n) * 100)}%` : '0%',
        color: dueCount ? signal.warn : signal.ok },
      { label: 'Véhicules confiés', num: String(n), pct: '100%', color: signal.ok },
    ];

    return {
      vehicleCount: n, segments, dueCount, okCount: n - dueCount,
      splitLabel: `${cnt('ok')} à jour · ${cnt('warn')} à surveiller · ${cnt('danger')} en retard`,
      openCount: open.length, pendingCount, pipeline, queue,
      pendingColor: pendingCount ? signal.danger : signal.ok,
      queueSub: open.length === 0
        ? 'Aucune demande ouverte'
        : `${open.length} demande${open.length > 1 ? 's' : ''} ouverte${open.length > 1 ? 's' : ''} — ${pendingCount} sans devis${
            oldestPendingH >= 24 ? `, la plus ancienne depuis ${Math.floor(oldestPendingH / 24)} j` : ''}`,
      money,
      load, bays,
      loadDash: `${(Math.min(100, load) / 100) * 314} 314`,
      loadColor: load >= 90 ? signal.danger : load >= 60 ? signal.warn : signal.ok,
      loadLabel: load >= 90 ? 'Saturé' : load >= 60 ? 'Chargé' : 'Disponible',
      loadNote: pendingCount
        ? `Deviser les ${pendingCount} demandes en attente remplirait ${Math.min(BAY_COUNT, inBay + pendingCount)} postes sur ${BAY_COUNT}.`
        : 'Aucune demande en attente de devis.',
    };
  }, [vehicles, requests, quotations, invoices]);
}
